import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { Loader2, UserPlus, Trash2, Shield, Search, Filter, Plus, X, Eye, Crown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AdminLogger } from "@/lib/adminLogger";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useSuperuser } from "@/hooks/useSuperuser";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import { BlacklistManager } from "@/components/BlacklistManager";
import { BlacklistedAccountsList } from "@/components/BlacklistedAccountsList";
import { PendingUsersList } from "@/components/PendingUsersList";

type AppRole = "admin" | "user" | "landlord" | "adjuster" | "client";

const ALL_ROLES: AppRole[] = ["user", "admin", "landlord", "adjuster", "client"];

interface UserWithRoles {
  id: string;
  email: string;
  roles: AppRole[];
  created_at: string;
}

const UserManagement = () => {
  const navigate = useNavigate();
  const { startImpersonation } = useImpersonation();
  const { isSuperuser } = useSuperuser();
  const { settings: systemSettings } = useSystemSettings();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<UserWithRoles[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState<string>("client");
  const [submitting, setSubmitting] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkRole, setBulkRole] = useState<string>("");
  const [bulkAction, setBulkAction] = useState<"add" | "remove">("add");
  const [emailFilter, setEmailFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [activityLog, setActivityLog] = useState<any[]>([]);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchActivityLog();
    }
  }, [isAdmin]);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();
      if (!roleData) {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
        return;
      }
      setIsAdmin(true);
    } catch (error: any) {
      console.error("Error checking auth:", error);
      toast.error("Authentication error");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select("id, email, created_at");
      if (profilesError) throw profilesError;

      const { data: roles, error: rolesError } = await supabase
        .from("user_roles")
        .select("user_id, role");
      if (rolesError) throw rolesError;

      const rolesByUser = new Map<string, AppRole[]>();
      for (const r of roles) {
        const existing = rolesByUser.get(r.user_id) || [];
        existing.push(r.role as AppRole);
        rolesByUser.set(r.user_id, existing);
      }

      const usersWithRoles: UserWithRoles[] = profiles.map(profile => ({
        ...profile,
        roles: rolesByUser.get(profile.id) || ["user"],
      }));

      setUsers(usersWithRoles);
    } catch (error: any) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users");
    }
  };

  const fetchActivityLog = async () => {
    try {
      const { data, error } = await supabase
        .from("user_role_history")
        .select("id, user_id, old_role, new_role, created_at, changed_by")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;

      const userIds = [...new Set([...data.map(d => d.user_id), ...data.map(d => d.changed_by)])];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, email")
        .in("id", userIds);

      const profileMap = profiles?.reduce((acc, p) => ({ ...acc, [p.id]: p.email }), {}) || {};
      setActivityLog(data.map(log => ({
        ...log,
        user_email: profileMap[log.user_id],
        changed_by_email: profileMap[log.changed_by]
      })));
    } catch (error: any) {
      console.error("Error fetching activity log:", error);
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail || !newUserPassword || !newUserRole) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUserEmail,
        password: newUserPassword,
        options: { emailRedirectTo: `${window.location.origin}/` }
      });
      if (authError) throw authError;
      if (!authData.user) throw new Error("User creation failed");

      const { error: roleError } = await supabase
        .from("user_roles")
        .insert({ user_id: authData.user.id, role: newUserRole as AppRole });
      if (roleError) throw roleError;

      await AdminLogger.userCreated(authData.user.id, newUserEmail, newUserRole);
      toast.success("User created successfully");
      setShowAddDialog(false);
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserRole("client");
      fetchUsers();
    } catch (error: any) {
      console.error("Error creating user:", error);
      toast.error(error.message || "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRole = async (userId: string, role: AppRole) => {
    if (role === "admin" && !isSuperuser) {
      toast.error("Only superusers can assign the admin role");
      return;
    }
    try {
      const { error } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role });
      if (error) throw error;

      const targetUser = users.find(u => u.id === userId);
      await AdminLogger.roleChange(userId, null, role, targetUser?.email);

      try {
        await supabase.functions.invoke('send-role-change-notification', {
          body: { userId, oldRole: null, newRole: role, changedBy: user?.id }
        });
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
      }

      toast.success(`Added "${role}" role`);
      fetchUsers();
      fetchActivityLog();
    } catch (error: any) {
      if (error.code === '23505') {
        toast.error("User already has this role");
      } else {
        console.error("Error adding role:", error);
        toast.error("Failed to add role");
      }
    }
  };

  const handleRemoveRole = async (userId: string, role: AppRole) => {
    if (role === "admin" && !isSuperuser) {
      toast.error("Only superusers can remove the admin role");
      return;
    }
    const targetUser = users.find(u => u.id === userId);
    if (targetUser && targetUser.roles.length <= 1) {
      toast.error("Cannot remove the last role. Users must have at least one role.");
      return;
    }

    try {
      const { error } = await supabase
        .from("user_roles")
        .delete()
        .eq("user_id", userId)
        .eq("role", role);
      if (error) throw error;

      await AdminLogger.roleChange(userId, role, null, targetUser?.email);

      try {
        await supabase.functions.invoke('send-role-change-notification', {
          body: { userId, oldRole: role, newRole: null, changedBy: user?.id }
        });
      } catch (emailError) {
        console.error('Failed to send notification email:', emailError);
      }

      toast.success(`Removed "${role}" role`);
      fetchUsers();
      fetchActivityLog();
    } catch (error: any) {
      console.error("Error removing role:", error);
      toast.error("Failed to remove role");
    }
  };

  const handleBulkRoleChange = async () => {
    if (selectedUserIds.length === 0) {
      toast.error("Please select at least one user");
      return;
    }
    if (!bulkRole) {
      toast.error("Please select a role");
      return;
    }
    setSubmitting(true);
    try {
      let successCount = 0;
      let errorCount = 0;

      for (const userId of selectedUserIds) {
        try {
          if (bulkAction === "add") {
            const { error } = await supabase
              .from("user_roles")
              .insert({ user_id: userId, role: bulkRole as AppRole });
            if (error) {
              if (error.code === '23505') continue; // already has role
              throw error;
            }
          } else {
            const targetUser = users.find(u => u.id === userId);
            if (targetUser && targetUser.roles.length <= 1) continue;
            const { error } = await supabase
              .from("user_roles")
              .delete()
              .eq("user_id", userId)
              .eq("role", bulkRole as AppRole);
            if (error) throw error;
          }
          successCount++;
        } catch {
          errorCount++;
        }
      }

      if (errorCount > 0) {
        toast.error(`Failed for ${errorCount} user(s)`);
      }
      if (successCount > 0) {
        toast.success(`${bulkAction === "add" ? "Added" : "Removed"} role for ${successCount} user(s)`);
      }

      setSelectedUserIds([]);
      setBulkRole("");
      fetchUsers();
      fetchActivityLog();
    } catch (error: any) {
      console.error("Error in bulk operation:", error);
      toast.error("Failed to update roles");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const [deleteTargetUser, setDeleteTargetUser] = useState<UserWithRoles | null>(null);
  const [deleteTargetClaims, setDeleteTargetClaims] = useState<any[]>([]);
  const [loadingTargetClaims, setLoadingTargetClaims] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const openDeleteDialog = async (u: UserWithRoles) => {
    setDeleteTargetUser(u);
    setDeleteTargetClaims([]);
    setLoadingTargetClaims(true);
    try {
      const { data, error } = await supabase
        .from("claims")
        .select("id, claim_number, claim_type, status, policyholder_name, created_at")
        .eq("user_id", u.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      setDeleteTargetClaims(data || []);
    } catch (err) {
      console.error("Error loading user claims:", err);
      toast.error("Failed to load user's submitted claims");
    } finally {
      setLoadingTargetClaims(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-user", {
        body: { targetUserId: userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("User deleted successfully");
      setDeleteTargetUser(null);
      setDeleteTargetClaims([]);
      fetchUsers();
    } catch (error: any) {
      console.error("Error deleting user:", error);
      toast.error(error.message || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "admin": return "destructive";
      case "landlord": return "default";
      case "adjuster": return "secondary";
      case "client": return "outline";
      default: return "outline";
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesEmail = u.email.toLowerCase().includes(emailFilter.toLowerCase());
    const matchesRole = roleFilter === "all" || u.roles.includes(roleFilter as AppRole);
    return matchesEmail && matchesRole;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold flex items-center gap-2">
              <Shield className="h-8 w-8" />
              User Management
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage user accounts and roles — users can have multiple roles
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>Add a new user account with an initial role</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="user@example.com" />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} placeholder="••••••••" />
                </div>
                <div>
                  <Label htmlFor="role">Initial Role</Label>
                  <Select value={newUserRole} onValueChange={setNewUserRole}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ALL_ROLES.map(r => (
                        <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleAddUser} disabled={submitting} className="w-full">
                  {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>) : "Create User"}
                </Button>
              </div>
            </DialogContent>
        </Dialog>
        </div>

        <PendingUsersList onChange={fetchUsers} />

        {/* Bulk Role Operations */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Bulk Role Operations</CardTitle>
            <CardDescription>Add or remove a role for multiple selected users at once</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div>
                <Label>Selected</Label>
                <p className="text-sm text-muted-foreground mt-1">{selectedUserIds.length} user(s)</p>
              </div>
              <div className="w-32">
                <Label>Action</Label>
                <Select value={bulkAction} onValueChange={(v) => setBulkAction(v as "add" | "remove")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add Role</SelectItem>
                    <SelectItem value="remove">Remove Role</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label>Role</Label>
                <Select value={bulkRole} onValueChange={setBulkRole}>
                  <SelectTrigger><SelectValue placeholder="Select role..." /></SelectTrigger>
                  <SelectContent>
                    {ALL_ROLES.map(r => (
                      <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleBulkRoleChange} disabled={selectedUserIds.length === 0 || !bulkRole || submitting}>
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating...</>) : (
                  <><Shield className="h-4 w-4 mr-2" />{bulkAction === "add" ? "Add" : "Remove"} Role</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* All Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Users</CardTitle>
            <CardDescription>
              Showing {filteredUsers.length} of {users.length} user{users.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-6">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search by email..." value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} className="pl-10" />
              </div>
              <div className="w-48">
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger>
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {ALL_ROLES.map(r => (
                      <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedUserIds.length === filteredUsers.filter(u => u.id !== user?.id).length && filteredUsers.length > 0}
                      onCheckedChange={() => {
                        if (selectedUserIds.length === filteredUsers.filter(u => u.id !== user?.id).length) {
                          setSelectedUserIds([]);
                        } else {
                          setSelectedUserIds(filteredUsers.filter(u => u.id !== user?.id).map(u => u.id));
                        }
                      }}
                      aria-label="Select all visible users"
                    />
                  </TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Roles</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => {
                  const availableRoles = ALL_ROLES.filter(r => !u.roles.includes(r as AppRole));
                  const isSelf = u.id === user?.id;
                  return (
                    <TableRow key={u.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedUserIds.includes(u.id)}
                          onCheckedChange={() => toggleUserSelection(u.id)}
                          disabled={isSelf}
                          aria-label={`Select ${u.email}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{u.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          {u.roles.map(role => (
                            <Badge key={role} variant={getRoleBadgeVariant(role)} className="gap-1 pr-1">
                              {role}
                              {!isSelf && u.roles.length > 1 && (
                                <button
                                  onClick={() => handleRemoveRole(u.id, role)}
                                  className="ml-0.5 rounded-full hover:bg-background/20 p-0.5"
                                  aria-label={`Remove ${role} role`}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              )}
                            </Badge>
                          ))}
                          {!isSelf && availableRoles.length > 0 && (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="inline-flex items-center justify-center rounded-full border border-dashed border-muted-foreground/40 h-6 w-6 hover:border-primary hover:text-primary transition-colors" aria-label="Add role">
                                  <Plus className="h-3 w-3" />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-36 p-1 bg-popover z-50" align="start">
                                {availableRoles.map(role => (
                                  <button
                                    key={role}
                                    onClick={() => handleAddRole(u.id, role)}
                                    className="w-full text-left px-3 py-1.5 text-sm rounded hover:bg-accent transition-colors"
                                  >
                                    {role.charAt(0).toUpperCase() + role.slice(1)}
                                  </button>
                                ))}
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{new Date(u.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right space-x-1">
                        {!isSelf && isSuperuser && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              AdminLogger.impersonationStart(u.id, u.email, u.roles);
                              startImpersonation({ id: u.id, email: u.email, roles: u.roles });
                              const primaryRole = u.roles[0];
                              const rolePaths: Record<string, string> = {
                                admin: "/admin",
                                landlord: "/landlord-dashboard",
                                adjuster: "/adjuster-dashboard",
                                client: "/client-dashboard",
                                user: "/dashboard",
                              };
                              navigate(rolePaths[primaryRole] || "/dashboard");
                            }}
                            title="Impersonate user"
                          >
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        {!isSelf && systemSettings.user_deletion_enabled && !u.roles.includes("admin") && (
                          <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(u)} title="Delete user">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <BlacklistManager />

        <BlacklistedAccountsList />

        {/* Recent Activity */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Recent role changes across all users</CardDescription>
          </CardHeader>
          <CardContent>
            {activityLog.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No activity to display</p>
            ) : (
              <div className="space-y-4">
                {activityLog.map((log) => (
                  <div key={log.id} className="flex items-center justify-between border-b pb-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{log.user_email || "Unknown user"}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={getRoleBadgeVariant(log.old_role || "user")} className="text-xs">
                          {log.old_role || "none"}
                        </Badge>
                        <span className="text-muted-foreground text-xs">→</span>
                        <Badge variant={getRoleBadgeVariant(log.new_role)} className="text-xs">
                          {log.new_role}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">Changed by: {log.changed_by_email || "System"}</p>
                      <p className="text-xs text-muted-foreground">{new Date(log.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <AlertDialog open={!!deleteTargetUser} onOpenChange={(open) => { if (!open) { setDeleteTargetUser(null); setDeleteTargetClaims([]); } }}>
          <AlertDialogContent className="max-w-2xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure you want to delete this user?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    This will permanently delete <strong>{deleteTargetUser?.email}</strong> and all associated data. This action cannot be undone.
                  </p>
                  <div className="rounded-md border bg-muted/40 p-3">
                    <p className="text-sm font-semibold mb-2">Submitted claims ({deleteTargetClaims.length})</p>
                    {loadingTargetClaims ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-3 w-3 animate-spin" /> Loading claims...
                      </div>
                    ) : deleteTargetClaims.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No claims submitted by this user.</p>
                    ) : (
                      <div className="max-h-48 overflow-y-auto space-y-1.5">
                        {deleteTargetClaims.map((c) => (
                          <div key={c.id} className="flex items-center justify-between gap-2 text-sm border-b last:border-b-0 pb-1.5 last:pb-0">
                            <div className="flex-1 min-w-0">
                              <span className="font-medium">{c.claim_number}</span>
                              <span className="text-muted-foreground"> — {c.policyholder_name} ({c.claim_type})</span>
                            </div>
                            <Badge variant="outline" className="text-xs">{c.status}</Badge>
                            <button
                              type="button"
                              onClick={() => { window.open(`/claim/${c.id}`, "_blank"); }}
                              className="text-xs text-primary hover:underline whitespace-nowrap"
                            >
                              Review
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleting || loadingTargetClaims}
                onClick={(e) => {
                  e.preventDefault();
                  if (deleteTargetUser) handleDeleteUser(deleteTargetUser.id);
                }}
              >
                {deleting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting...</>) : "Yes, delete user"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default UserManagement;
